import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useCombobox } from "downshift";

import {
  type FormSelectChoice,
  type InputSelectOnKeyDown,
  type FieldId,
  type ExtendedFieldId,
} from "../../../lib/types/types";
import { setRef } from "../../../lib/refs/refs";

import { InputSelectListComponent } from "./InputSelectList/InputSelectListComponent";
import { InputSelectButtonComponent } from "./InputSelectButton/InputSelectButtonComponent";

const itemToString = (item: FormSelectChoice) => (item ? item.label : "");

const filterOptions = (options: FormSelectChoice[], inputValue: string): FormSelectChoice[] => {
  const filteredOptions: FormSelectChoice[] = options.filter(
    (option) =>
      !inputValue ||
      option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.value.toString().toLowerCase().includes(inputValue.toLowerCase()),
  );
  return filteredOptions;
};

export interface InputSelectProps {
  onChange: any;
  options: FormSelectChoice[];
  fieldId: FieldId | ExtendedFieldId;
  fieldLabelId?: string;
  className?: string;
  disabled?: boolean;
  inputId?: string;
  isErrored?: boolean;
  onKeyDown?: InputSelectOnKeyDown;
  placeholder?: string;
  suppressPlaceholder?: boolean;
  value?: FormSelectChoice;
  autoFocus?: boolean;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  isRequired?: boolean;
  isLoading?: boolean;
  buttonRef?: string;
  role?: string;
  autoComplete?: string;
}

const InputSelect = ({
  className,
  inputId,
  disabled = false,
  fieldId,
  fieldLabelId,
  isErrored = false,
  options,
  onKeyDown,
  onChange,
  placeholder = "",
  // Most invokers want placeholder hidden by css, so it can be easily shown when
  // customizing the implementation. Birth date is an example of an exception.
  suppressPlaceholder = true,
  value,
  autoFocus,
  ariaLabel,
  ariaDescribedBy,
  isRequired,
  isLoading = false,
  role = "combobox",
  autoComplete,
}: InputSelectProps) => {
  const [inputState, setInputState] = useState("");
  const items = inputState ? filterOptions(options, inputState) : options;

  const {
    getInputProps,
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    isOpen,
    selectItem,
  } = useCombobox<FormSelectChoice>({
    id: inputId,
    items,
    onInputValueChange({ inputValue }) {
      setInputState(inputValue);
    },
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => onChange(newSelectedItem),
    itemToString,
    selectedItem: value && value.value && value.label ? value : null,
    defaultHighlightedIndex: 0,
  });

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeof onKeyDown === "function") {
      onKeyDown(event);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  // NOTE: Dependency array omitted to account for use of resetRef(), as useEffect with dep arr
  // will only trigger on initial render leaving the refs empty, this replicates prior version
  // where ref callback would trigger with each re-render
  useEffect(() => {
    setRef(fieldId, inputRef.current);
  });

  return (
    <div className={`sid-input-select-list ${className || ""}`}>
      <input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getInputProps({
          ref: inputRef,
          onKeyDown: (event) => handleOnKeyDown(event),
          "aria-labelledby": fieldLabelId,
        })}
        // TODO resolve next line. Product needs this to autofocus, but linter says bad for accessibility
        // eslint-disable-next-line
        autoFocus={autoFocus}
        id={inputId}
        className={`sid-l-full-width sid-text-input ${
          suppressPlaceholder ? "sid-hidden-placeholder" : ""
        } ${isErrored ? "sid-text-input--error" : ""}`}
        disabled={disabled}
        placeholder={placeholder}
        aria-required={isRequired}
        aria-describedby={ariaDescribedBy}
        aria-invalid={isErrored}
        role={role}
        autoComplete={autoComplete}
      />

      <InputSelectButtonComponent
        disabled={disabled}
        getToggleButtonProps={getToggleButtonProps}
        isOpen={isOpen}
        isClearable={!!value?.value}
        clearSelection={() => selectItem(undefined)}
      />

      <InputSelectListComponent
        highlightedIndex={highlightedIndex}
        itemToString={itemToString}
        options={items}
        getItemProps={getItemProps}
        getMenuProps={getMenuProps}
        ariaLabel={ariaLabel}
        isOpen={isOpen}
        isLoading={isLoading}
      />
    </div>
  );
};

export const InputSelectComponent = InputSelect;
